import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import {
  Package,
  MapPin,
  Clock,
  User,
  Mail,
  Phone,
  CreditCard,
  ArrowRight,
  Ruler,
  Timer,
  Thermometer,
  DoorOpen,
  RotateCcw,
  KeyRound,
  PenLine,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatTimeRange } from "../../../pages/SendPackagePage/components/sections/SectionHeader"
import { BusTransportSection } from '../../../pages/SendPackagePage/components/BusTransportSection';


export const DeliveryDetailsModal = ({ open, onClose, delivery }) => {
  const { t } = useTranslation();

  if (!delivery) return null;

  const getDeliveryId = (d) => d?.id || d?.deliveryId || "—";

  const originInfo = delivery?.originInfo || delivery?.fromInfo || null;
  const destinationInfo = delivery?.destinationInfo || delivery?.toInfo || null;
  const originDetails = originInfo?.details || {};
  const destinationDetails = destinationInfo?.details || {};

  const fromAddress =
    originInfo?.address ||
    (typeof delivery?.origin === "string" ? delivery.origin : null) ||
    "—";
  const toAddress =
    destinationInfo?.address ||
    (typeof delivery?.destination === "string" ? delivery.destination : null) ||
    "—";

  const pickupTime =
    delivery?.pickupTime || delivery?.pickUpTime || delivery?.createdAtMs;
  const targetTime = delivery?.targetTime || delivery?.createdAtMs;
  const latestPickUpTime =
    delivery?.latestPickUpTime || delivery?.latestPickupTime;
  const earliestTargetTime = delivery?.earliestTargetTime;

  const senderName = delivery?.senderInfo?.name || "";
  const senderEmail = delivery?.senderInfo?.email || "";
  const senderPhone = delivery?.senderPhone || "";
  const recipientName = delivery?.receiverInfo?.name || "";
  const recipientEmail = delivery?.receiverInfo?.email || "";
  const recipientPhone = delivery?.receiverPhone || "";



  const getStatusColor = (s) =>
    ({
      active: "warning",
      ongoing: "info",
      accepted: "info",
      picked: "primary",
      completed: "success",
      cancelled: "error",
    }[s] || "default");
  const getStatusLabel = (s) =>
    ({
      active: t("delivery.status.active"),
      ongoing: t("delivery.status.ongoing"),
      accepted: t("delivery.status.accepted"),
      picked: t("delivery.status.picked"),
      completed: t("delivery.status.completed"),
      cancelled: t("delivery.status.cancelled"),
    }[s] || s);

  // ── Reusable sub-components ────────────────────────────────────────────────

  const SectionTitle = ({ icon, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          bgcolor: "primary.50",
          border: "1.5px solid",
          borderColor: "primary.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {children}
      </Typography>
    </Box>
  );

  const Field = ({ label, value, xs = 6 }) =>
    value ? (
      <Grid item xs={xs}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Grid>
    ) : null;

  const ContactBlock = ({ nameLabel, name, phone, email }) => (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {nameLabel}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {name || "—"}
      </Typography>
      {phone && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
          <Phone size={12} />
          <Typography variant="caption" color="text.secondary">
            {phone}
          </Typography>
        </Box>
      )}
      {email && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Mail size={12} />
          <Typography variant="caption" color="text.secondary">
            {email}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const AddressBlock = ({ label, address, details, instructions }) => (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "background.default",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {address}
      </Typography>
      <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {details.apartment && (
          <Chip
            label={`Apt ${details.apartment}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        )}
        {details.floor && (
          <Chip
            label={`Floor ${details.floor}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        )}
        {details.building && (
          <Chip
            label={details.building}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        )}
        {details.accessCode && (
          <Chip
            label={`Code: ${details.accessCode}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        )}
      </Box>
      {instructions && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.75, fontStyle: "italic" }}
        >
          "{instructions}"
        </Typography>
      )}
    </Box>
  );

  // ── Special options ────────────────────────────────────────────────────────
  const specialOptions = [
    {
      key: "canBeLeftAtDoor",
      active: delivery.canBeLeftAtDoor,
      icon: <DoorOpen size={12} />,
      label: t("delivery.canBeLeftAtDoor") || "Leave at Door",
    },
    {
      key: "returnParcels",
      active: delivery.returnParcels,
      icon: <RotateCcw size={12} />,
      label: t("delivery.returnParcels") || "Returns",
    },
    {
      key: "temperatureControl",
      active: delivery.temperatureControl,
      icon: <Thermometer size={12} />,
      label: t("delivery.temperatureControl") || "Temp Control",
    },
    {
      key: "requiresPin",
      active: delivery.requiresPin,
      icon: <KeyRound size={12} />,
      label: t("delivery.requiresPin") || "PIN",
    },
    {
      key: "requiresSignature",
      active: delivery.requiresSignature,
      icon: <PenLine size={12} />,
      label: t("delivery.requiresSignature") || "Signature",
    },
  ].filter((o) => o.active);

  const showBusTransportSection = delivery?.busTransportData?.price

  const courierName  = delivery?.courierName  || "";
  const courierPhone = delivery?.courierPhone || "";

  const isOngoingOrPickedUp = ["ongoing", "picked"].includes(delivery.status);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      {/* ── Title ── */}
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "primary.50",
                border: "1.5px solid",
                borderColor: "primary.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Package size={20} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {t("delivery.deliveryDetails")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getDeliveryId(delivery)}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip
              label={getStatusLabel(delivery.status || "active")}
              color={getStatusColor(delivery.status || "active")}
              size="small"
            />
            {delivery?.deliveryTransportType && (
              <Chip
                label={delivery.deliveryTransportType}
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* ── Route ── */}
          <Box>
            <SectionTitle icon={<MapPin size={14} />}>
              {t("delivery.locations") || "Route"}
            </SectionTitle>
            <Stack spacing={1.5}>
              <AddressBlock
                label={t("delivery.from") || "Pickup"}
                address={fromAddress}
                details={originDetails}
                instructions={
                  originDetails?.instructions || delivery?.fromExtraInfo
                }
              />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, pl: 1.5 }}
              >
                <ArrowRight
                  size={16}
                  color="var(--mui-palette-text-disabled)"
                />
                {/* {distanceKm != null && (
                  <Typography variant="caption" color="text.disabled">
                    {distanceKm.toFixed(1)} km
                    {durationMin != null
                      ? ` · ~${Math.round(durationMin)} min`
                      : ""}
                  </Typography>
                )} */}
              </Box>
              <AddressBlock
                label={t("delivery.to") || "Delivery"}
                address={toAddress}
                details={destinationDetails}
                instructions={
                  destinationDetails?.instructions || delivery?.toExtraInfo
                }
              />
            </Stack>
          </Box>

          <Divider />
          {showBusTransportSection && (
            <>
              <Box>
                <BusTransportSection
                  busTransportData={delivery?.busTransportData}
                  disableEdit
                  onEditBusOption={()=>{}}
                  inModal={true}
                />
              </Box>
              <Divider />
              
              </>
            )}
          {/* ── Timing ── */}
          <Box>
            <SectionTitle icon={<Clock size={14} />}>
              {t("delivery.timing") || "Timing"}
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    {t("delivery.pickup") || "Pickup"}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatTimeRange(
                      pickupTime,
                      delivery.hasTimeWindow ? latestPickUpTime : null
                    ) || "—"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    {t("delivery.delivery") || "Delivery"}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatTimeRange(
                      delivery.hasTimeWindow ? earliestTargetTime : targetTime,
                      targetTime && delivery.hasTimeWindow ? targetTime : null
                    ) || "—"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* ── People ── */}
          <Box>
            <SectionTitle icon={<User size={14} />}>
              {t("delivery.people") || "People"}
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ContactBlock
                  nameLabel={t("delivery.sender") || "Sender"}
                  name={senderName}
                  phone={senderPhone}
                  email={senderEmail}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ContactBlock
                  nameLabel={t("delivery.recipient") || "Recipient"}
                  name={recipientName}
                  phone={recipientPhone}
                  email={recipientEmail}
                />
              </Grid>


              {courierName && isOngoingOrPickedUp &&(
      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {t("delivery.courier") || "Kuriiri"}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {courierName}
          </Typography>
          {courierPhone ? (
            <Box sx={{ mt: 0.75 }}>
              <Button
                variant="contained"
                disableElevation
                size="small"
                startIcon={<Phone size={13} />}
                component="a"
                href={`tel:${courierPhone}`}
                sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8125rem" }}
              >
                {courierPhone}
              </Button>
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
              Ei puhelinnumeroa
            </Typography>
          )}

</Box>
      </Grid>    )}
            </Grid>
          </Box>

          {/* ── Package ── */}
          {(delivery.packageSize || delivery.weightKg != null) && (
            <>
              <Divider />
              <Box>
                <SectionTitle icon={<Package size={14} />}>
                  {t("delivery.packageInfo") || "Package"}
                </SectionTitle>
                <Grid container spacing={2}>
                  <Field
                    label={t("delivery.size") || "Size"}
                    value={
                      delivery.packageSize
                        ? t(`delivery.${delivery.packageSize}`)
                        : null
                    }
                  />
                  <Field
                    label={t("delivery.weight") || "Weight"}
                    value={
                      delivery.weightKg != null
                        ? `${delivery.weightKg} kg`
                        : null
                    }
                  />
                </Grid>
              </Box>
            </>
          )}

          {/* ── Special options ── */}
          {specialOptions.length > 0 && (
            <>
              <Divider />
              <Box>
                <SectionTitle icon={<Package size={14} />}>
                  {t("delivery.specialOptions") || "Special Options"}
                </SectionTitle>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {specialOptions.map((opt) => (
                    <Chip
                      key={opt.key}
                      icon={opt.icon}
                      label={opt.label}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 26,
                        fontSize: "0.75rem",
                        "& .MuiChip-icon": { ml: "6px" },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* ── Return parcel link ── */}
          {delivery.returnLink && (
            <>
              <Divider />
              <Box>
                <SectionTitle icon={<RotateCcw size={14} />}>
                  {t("delivery.returnParcel") || "Palautuslähetys"}
                </SectionTitle>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {delivery.returnLink}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      navigator.clipboard.writeText(delivery.returnLink)
                    }
                    sx={{ flexShrink: 0 }}
                  >
                    Kopioi
                  </Button>
                </Box>
              </Box>
            </>
          )}

          {/* ── Billing ── */}
          {delivery.billing && (
            <>
              <Divider />
              <Box>
                <SectionTitle icon={<CreditCard size={14} />}>
                  {t("delivery.billing") || "Billing"}
                </SectionTitle>
                <Grid container spacing={2}>
                  <Field
                    label={t("delivery.paymentMethod") || "Method"}
                    value={
                      delivery.billing?.paymentMethod
                        ? t(`delivery.${delivery.billing.paymentMethod}`)
                        : null
                    }
                  />
                  <Field
                    label={t("delivery.price") || "Price"}
                    value={
                      typeof delivery.price === "number"
                        ? `€${delivery.price.toFixed(2)}`
                        : null
                    }
                  />
                  <Field
                    label={t("delivery.company") || "Company"}
                    value={delivery.billing.companyName}
                    xs={12}
                  />
                  <Field
                    label={t("delivery.yNumber") || "Business ID"}
                    value={delivery.billing.yNumber}
                  />
                  <Field
                    label={t("delivery.paymentReference") || "Reference"}
                    value={delivery.billing.paymentReference}
                  />
                  <Field
                    label={t("delivery.billingDepartment") || "Department"}
                    value={delivery.billing.billingDepartment}
                    xs={12}
                  />
                </Grid>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t("common.close") || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
